<template>
  <p-product-detail-block
    v-if="hasProductSheetUrls || hasProductManualUrls || hasSafetySheetUrls"
    data-test="documentation"
    :title="i18n._('Documentation')"
  >
    <div v-if="hasSafetySheetUrls && product.type === 'Pack'">
      <div
        class="product-detail-block-documentation__link"
        v-for="safetySheet in packSafetySheetUrl"
        :key="'safety-sheet-' + safetySheet.product_name"
        @click="
          onDocumentSheetDownload('click_download_security_file', { file_name: getFileNameFromUrl(safetySheet.value) })
        "
      >
        <adn-button
          kind="link"
          markup="a"
          :href="safetySheet.value"
          icon-name="function-see-pdf"
          icon-position="left"
          target="_blank"
          rel="external"
          data-test="documentation-safety-sheet-link"
        >
          {{ i18n._('Fiche de données de sécurité du produit') }} {{ suffix }}: {{ safetySheet.product_name }}
        </adn-button>
      </div>
    </div>
    <div v-if="hasSafetySheetUrls && product.type !== 'Pack'">
      <div
        class="product-detail-block-documentation__link"
        v-for="(safetySheetUrl, index) in product.safetySheetUrls"
        :key="'safety-sheet-' + index"
        @click="
          onDocumentSheetDownload('click_download_security_file', { file_name: getFileNameFromUrl(safetySheetUrl) })
        "
      >
        <adn-button
          kind="link"
          markup="a"
          :href="safetySheetUrl"
          icon-name="function-see-pdf"
          icon-position="left"
          target="_blank"
          rel="external"
          data-test="documentation-safety-sheet-link"
        >
          {{ i18n._('Fiche de données de sécurité') }} {{ suffix }}
        </adn-button>
      </div>
    </div>
    <div v-if="hasProductSheetUrls">
      <div
        class="product-detail-block-documentation__link"
        v-for="(productSheetUrl, index) in product.productSheetUrls"
        :key="'fiche' + index"
        @click="
          onDocumentSheetDownload('click_download_product_file', { file_name: getFileNameFromUrl(productSheetUrl) })
        "
      >
        <adn-button
          kind="link"
          markup="a"
          :href="productSheetUrl"
          icon-name="function-see-pdf"
          icon-position="left"
          target="_blank"
          rel="external"
          data-test="documentation-sheet-link"
        >
          {{ i18n._('Fiche produit') }}
        </adn-button>
      </div>
    </div>
    <div v-if="hasProductManualUrls">
      <div
        class="product-detail-block-documentation__link"
        v-for="(productManualUrl, index) in product.productManualUrls"
        :key="'notice' + index"
        @click="
          onDocumentSheetDownload('click_download_technical_file', { file_name: getFileNameFromUrl(productManualUrl) })
        "
      >
        <adn-button
          kind="link"
          markup="a"
          :href="productManualUrl"
          icon-name="function-see-pdf"
          icon-position="left"
          target="_blank"
          rel="external"
          data-test="documentation-manual-link"
        >
          {{ i18n._('Notice produit') }}
        </adn-button>
      </div>
    </div>
  </p-product-detail-block>
</template>

<script lang="ts">
import { i18n } from '@b2ag/locale'
import { type PropType, computed, defineComponent } from 'vue'
import { useAnalyticService } from '@b2ag/plugin-aladin-gtm'
import PProductDetailBlock from '../PProductDetailBlock/PProductDetailBlock.vue'
import type { Product } from '../typings'
import AdnButton from '@b2ag/design-system/src/components/AdnButton/AdnButton.vue'

export default defineComponent({
  name: 'PProductDetailBlockDocumentation',
  props: {
    product: {
      type: Object as PropType<Product>,
      default: () => ({}),
    },
    suffix: {
      type: String,
      default: '',
    },
  },
  components: {
    PProductDetailBlock,
    AdnButton,
  },
  setup(props) {
    const { product } = props

    const hasProductSheetUrls = computed(() => product?.productSheetUrls?.length)
    const hasProductManualUrls = computed(() => product?.productManualUrls?.length)
    const hasSafetySheetUrls = computed(() => product?.safetySheetUrls?.length)
    const packSafetySheetUrl = computed(() =>
      product?.safetySheetUrls?.map((safetySheet) => ({
        product_name: safetySheet.product_name,
        value: safetySheet.value && safetySheet.value[0],
      })),
    )

    const onDocumentSheetDownload = (eventName, payload) => useAnalyticService().trackEvent(eventName, payload)
    const getFileNameFromUrl = (url: string) => url.split('/').pop()

    return {
      hasProductSheetUrls,
      hasProductManualUrls,
      hasSafetySheetUrls,
      packSafetySheetUrl,
      onDocumentSheetDownload,
      getFileNameFromUrl,
      i18n,
    }
  },
})
</script>

<style scoped lang="scss">
.product-detail-block-documentation__link {
  padding-left: 0;
  padding-right: 0;
  line-height: 2em;
}
</style>
